import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  MandalaIcon,
  TreeOfLifeIcon,
  LotusIcon,
  AncientSunIcon,
  EternalKnotIcon,
  CompassIcon,
  GoldenSpiralIcon,
  MetatronIcon,
  VesicaIcon,
  MairyIcon,
  SacredFounderIcon,
  TomeMachineIcon
} from '../components/ui/legendary-icons';
import { SparkleIcon, BelongingIcon, BalanceIcon, StewardIcon } from '../components/ui/icons';
import { EcosystemNav } from '../components/ui/ecosystem-nav';
import { 
  useAuth, 
  AuthModal, 
  ProjectsManager, 
  getUserName 
} from '../components/ui/auth-components';
import { ArrowRight, Compass, ChevronDown, Feather } from 'lucide-react';

// Simple icons for landing page
const UsersIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const LogInIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
    <polyline points="10 17 15 12 10 7"/>
    <line x1="15" y1="12" x2="3" y2="12"/>
  </svg>
);

const HeartIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);

const ShieldIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BELONGING_SESSIONS_URL = process.env.REACT_APP_BELONGING_SESSIONS_URL || process.env.REACT_APP_BACKEND_URL || '';

// Floating particles component
const FloatingParticles = () => {
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    const newParticles = [...Array(25)].map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 15 + 10,
      delay: Math.random() * 5,
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full bg-[var(--primary)]/30"
          style={{
            width: p.size,
            height: p.size,
            left: `${p.x}%`,
            top: `${p.y}%`,
          }}
          animate={{
            y: [-20, -100],
            opacity: [0, 0.8, 0],
            scale: [0.5, 1, 0.5],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// Animated sacred geometry background
const SacredGeometryBg = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    {/* Rotating outer mandala */}
    <motion.div
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.03]"
      animate={{ rotate: 360 }}
      transition={{ duration: 120, repeat: Infinity, ease: "linear" }}
    >
      <MandalaIcon size={1200} className="text-white" />
    </motion.div>
    
    {/* Counter-rotating inner mandala */}
    <motion.div
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.02]"
      animate={{ rotate: -360 }}
      transition={{ duration: 180, repeat: Infinity, ease: "linear" }}
    >
      <MetatronIcon size={800} className="text-[var(--accent)]" />
    </motion.div>
  </div>
);

// Animated compass scroll indicator
const CompassScrollIndicator = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 2 }}
    className="absolute bottom-8 flex flex-col items-center gap-2"
  >
    <motion.div
      animate={{ y: [0, 8, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      className="relative"
    >
      {/* Compass rose indicator */}
      <svg width="32" height="32" viewBox="0 0 32 32" className="text-white/40">
        <circle cx="16" cy="16" r="14" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3" />
        <path d="M16 4 L18 14 L16 16 L14 14 Z" fill="currentColor" opacity="0.8" />
        <path d="M16 28 L18 18 L16 16 L14 18 Z" fill="currentColor" opacity="0.4" />
        <path d="M4 16 L14 14 L16 16 L14 18 Z" fill="currentColor" opacity="0.4" />
        <path d="M28 16 L18 14 L16 16 L18 18 Z" fill="currentColor" opacity="0.4" />
        <circle cx="16" cy="16" r="2" fill="currentColor" opacity="0.6" />
      </svg>
    </motion.div>
    <motion.div
      animate={{ opacity: [0.3, 0.7, 0.3] }}
      transition={{ duration: 2, repeat: Infinity }}
    >
      <ChevronDown size={20} className="text-white/40" />
    </motion.div>
  </motion.div>
);

export default function LandingPage({ onEnter }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProjectsManager, setShowProjectsManager] = useState(false);
  
  // Auth hook
  const { isAuthenticated, user, userId, userName, logout } = useAuth();

  useEffect(() => {
    setTimeout(() => setShowContent(true), 300);
    
    // Check for SSO callback parameters
    const bs_user = searchParams.get('bs_user');
    const bs_token = searchParams.get('bs_token');
    const bs_time = searchParams.get('bs_time');
    
    if (bs_user && bs_token && bs_time) {
      handleSSOCallback(bs_user, bs_token, bs_time);
    }
  }, [searchParams]);

  const handleSSOCallback = async (bs_user, bs_token, bs_time) => {
    setSsoLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/sso/welcome?bs_user=${encodeURIComponent(bs_user)}&bs_token=${encodeURIComponent(bs_token)}&bs_time=${encodeURIComponent(bs_time)}`);
      const data = await response.json();
      
      if (data.valid && data.user) {
        // Store user info and proceed
        localStorage.setItem('blissabode_user_name', data.user.name);
        localStorage.setItem('blissabode_user_id', data.user.id);
        localStorage.setItem('blissabode_user_source', 'belonging_sessions');
        toast.success(`Welcome back, ${data.user.name}!`);
        onEnter();
      } else {
        toast.error('SSO validation failed. Please try again.');
      }
    } catch (error) {
      console.error('SSO error:', error);
      toast.error('Failed to authenticate with Belonging Sessions');
    } finally {
      setSsoLoading(false);
    }
  };

  const handleAuthSuccess = (user) => {
    toast.success(`Welcome, ${user.name}!`);
  };

  const handleBelongingSessionsLogin = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sso/login-url`);
      const data = await response.json();
      
      if (data.login_url) {
        window.open(data.login_url, '_blank');
        toast.info('Opening Belonging Sessions...');
      } else if (data.api_url) {
        window.open(data.api_url, '_blank');
        toast.info('Opening Belonging Sessions...');
      } else {
        // Direct fallback
        window.open('https://belongingsessions.com', '_blank');
        toast.info('Opening Belonging Sessions...');
      }
    } catch (error) {
      console.error('Failed to get login URL:', error);
      // Fallback to direct URL
      window.open('https://belongingsessions.com', '_blank');
      toast.info('Opening Belonging Sessions...');
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setIsSubscribing(true);
    try {
      const response = await fetch(`${API_URL}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!response.ok) throw new Error('Failed to subscribe');

      const data = await response.json();
      setSubscribed(true);
      toast.success('Welcome to the journey! You\'ve been added to the Belonging Sessions family.');
    } catch (error) {
      console.error('Subscribe error:', error);
      setSubscribed(true);
      toast.success('Welcome to the journey!');
    } finally {
      setIsSubscribing(false);
    }
  };

  // Updated features - The Eight Foundations (BAST expanded + Heart/Soul + Systems)
  const features = [
    {
      icon: HeartIcon,
      title: "Belonging",
      description: "The foundation of presence — creating spaces where every member feels seen, heard, and valued. Root your community in physiological safety."
    },
    {
      icon: Compass,
      title: "Agency",
      description: "Honor individual sovereignty while serving collective purpose. Every voice matters, every choice counts in shaping shared direction."
    },
    {
      icon: ShieldIcon,
      title: "Security",
      description: "Build trust through clear agreements, transparent processes, and sacred boundaries that protect both individuals and the whole."
    },
    {
      icon: UsersIcon,
      title: "Trust",
      description: "The currency of thriving communities. Cultivate reliability, consistency, and mutual accountability that deepens over time."
    },
    {
      icon: LotusIcon,
      title: "Heart & Soul",
      description: "The inner compass — shared values, collective vision, and the deeper 'why' that calls your community into existence."
    },
    {
      icon: BalanceIcon,
      title: "Governance",
      description: "Craft decision-making structures that honor collective wisdom while preventing power concentration. Consent over consensus."
    },
    {
      icon: EternalKnotIcon,
      title: "Connection",
      description: "Weave the threads between individual and collective — unity in diversity, strength in connection, autonomy within community."
    },
    {
      icon: GoldenSpiralIcon,
      title: "Systems & Logistics",
      description: "The practical architecture — resource flows, communication channels, and operational rhythms that sustain daily life."
    }
  ];

  // The 8-Point Business Compass for Startup Studio
  const startupCompassPoints = [
    { direction: 'N', title: 'Vision & Purpose', desc: 'Your North Star' },
    { direction: 'NE', title: 'Team & Culture', desc: 'First Who, Then What' },
    { direction: 'E', title: 'Business Model', desc: 'Value Creation' },
    { direction: 'SE', title: 'Market Analysis', desc: 'Know Your Territory' },
    { direction: 'S', title: 'Validation', desc: 'Evidence & Traction' },
    { direction: 'SW', title: 'Go-to-Market', desc: 'Customer Journey' },
    { direction: 'W', title: 'Execution', desc: 'Milestones & Metrics' },
    { direction: 'NW', title: 'Financials', desc: 'Sustainable Growth' },
  ];

  const pillars = [
    { name: 'Belonging', icon: HeartIcon },
    { name: 'Agency', icon: CompassIcon },
    { name: 'Security', icon: ShieldIcon },
    { name: 'Trust', icon: UsersIcon }
  ];

  if (ssoLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a1a0f] via-[#0d2818] to-[#0a1a0f] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <MandalaIcon size={80} className="text-[var(--primary)]" />
        </motion.div>
        <p className="text-white/70 ml-4">Authenticating with Belonging Sessions...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1a0f] via-[#0d2818] to-[#0a1a0f] text-white overflow-hidden">
      <FloatingParticles />
      <SacredGeometryBg />
      
      {/* Decorative background glows */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[var(--primary)]/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[var(--accent)]/6 rounded-full blur-[100px]" />
        <div className="absolute top-1/3 right-1/3 w-[400px] h-[400px] bg-[#2b6b69]/5 rounded-full blur-[80px]" />
      </div>

      {/* Top Navigation Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MandalaIcon size={32} className="text-[var(--primary)]" />
            <span className="font-heading text-lg text-white/90 hidden sm:inline">Bliss Abode</span>
          </div>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => setShowProjectsManager(true)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-sm text-white/80"
                >
                  <span className="hidden sm:inline">My Projects</span>
                  <span className="sm:hidden">Projects</span>
                </button>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <span className="text-xs text-emerald-400">{userName?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  <span className="text-sm text-emerald-300 hidden sm:inline">{userName}</span>
                </div>
                <button
                  onClick={logout}
                  className="text-xs text-white/40 hover:text-white/70 transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Button
                size="sm"
                onClick={() => setShowAuthModal(true)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white"
              >
                <LogInIcon size={16} className="mr-1.5" />
                Sign In
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-4 py-20">
        <AnimatePresence>
          {showContent && (
            <>
              {/* Animated Mandala Logo */}
              <motion.div
                initial={{ scale: 0, rotate: -180, opacity: 0 }}
                animate={{ scale: 1, rotate: 0, opacity: 1 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
                className="mb-8 relative"
              >
                <div className="relative">
                  {/* Pulsing glow effect */}
                  <motion.div
                    className="absolute inset-0 blur-2xl"
                    animate={{ opacity: [0.3, 0.6, 0.3], scale: [0.9, 1.1, 0.9] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <MandalaIcon size={200} className="text-[var(--primary)]" />
                  </motion.div>
                  
                  {/* Rotating outer ring */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 opacity-40"
                  >
                    <MandalaIcon size={200} className="text-[var(--primary)]" />
                  </motion.div>
                  
                  {/* Main icon */}
                  <MandalaIcon size={200} className="text-white relative z-10" />
                </div>
              </motion.div>

              {/* Title */}
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="text-center mb-6"
              >
                <h1 className="font-heading text-6xl md:text-8xl lg:text-9xl font-bold mb-4 tracking-tight">
                  <span className="bg-gradient-to-r from-white via-[#a8d5ba] to-white bg-clip-text text-transparent drop-shadow-2xl">
                    Bliss Abod
                  </span>
                  <span className="relative inline-block">
                    <span className="bg-gradient-to-r from-white via-[#a8d5ba] to-white bg-clip-text text-transparent drop-shadow-2xl">e</span>
                    <span className="absolute -top-1 -right-1 md:-top-2 md:-right-2 transform rotate-12">
                      <span className="inline-flex items-center px-1.5 py-0.5 md:px-2 md:py-1 rounded-md text-[9px] md:text-[11px] font-bold tracking-wide bg-gradient-to-r from-amber-300 to-amber-400 text-amber-900 shadow-lg shadow-amber-400/30 border border-amber-200/50">
                        BETA
                      </span>
                    </span>
                  </span>
                </h1>
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.8, duration: 0.8 }}
                  className="h-px bg-gradient-to-r from-transparent via-[var(--primary)] to-transparent mx-auto max-w-lg"
                />
              </motion.div>

              {/* NEW: App Description - Before the formula */}
              <motion.p
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="text-lg md:text-xl text-white/70 text-center max-w-2xl mb-6 font-light"
              >
                A member-owned app for designing communities and start-ups that last!
              </motion.p>

              {/* Main Tagline - Formula */}
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="text-center mb-6"
              >
                <p className="text-2xl md:text-3xl lg:text-4xl text-white/90 font-heading tracking-wide">
                  <span className="text-[var(--primary)]">building</span>
                  <span className="text-white/50 mx-3">+</span>
                  <span className="text-[var(--accent)]">belonging</span>
                  <span className="text-white/50 mx-3">=</span>
                  <span className="text-white">bliss abode</span>
                </p>
              </motion.div>

              <motion.p
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="text-sm text-[var(--primary)]/70 text-center mb-10"
              >
                Part of the Belonging Sessions Family
              </motion.p>

              {/* Four Pillars */}
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.8 }}
                className="flex flex-wrap justify-center gap-3 mb-12"
              >
                {pillars.map((pillar, i) => (
                  <motion.div
                    key={pillar.name}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 1 + i * 0.1, type: "spring", stiffness: 200 }}
                    className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-white/80 text-sm backdrop-blur-sm bg-white/5 hover:bg-white/10 hover:border-[var(--primary)]/40 transition-all duration-300"
                  >
                    <pillar.icon size={14} className="text-[var(--primary)]" />
                    {pillar.name}
                  </motion.div>
                ))}
              </motion.div>

              {/* CTA Buttons - Main Actions Side by Side */}
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.8 }}
                className="flex flex-col md:flex-row gap-4 mb-6 justify-center"
              >
                <Button
                  size="lg"
                  onClick={onEnter}
                  className="bg-[var(--primary)] hover:bg-[var(--primary-600)] text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-[var(--primary)]/30 group"
                  data-testid="enter-app-button"
                >
                  <TreeOfLifeIcon size={24} className="mr-2" />
                  Begin Your Charter
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                
                <Button
                  size="lg"
                  onClick={() => navigate('/startup')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-indigo-500/30 group"
                  data-testid="startup-studio-button"
                >
                  <SacredFounderIcon size={24} className="mr-2" />
                  Startup Studio
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                
                <Button
                  size="lg"
                  onClick={() => navigate('/tome')}
                  className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-violet-500/30 group"
                  data-testid="tome-machine-button"
                >
                  <TomeMachineIcon size={24} className="mr-2" />
                  Tome Machine
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </motion.div>
              
              {/* Studio Descriptions */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.3, duration: 0.8 }}
                className="max-w-3xl mx-auto mb-8"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="p-4 rounded-xl bg-[var(--primary)]/10 border border-[var(--primary)]/20 backdrop-blur-sm">
                    <h4 className="font-semibold text-[var(--primary)] mb-2 flex items-center gap-2">
                      <Compass size={16} />
                      Community Charter
                    </h4>
                    <p className="text-white/60 text-xs leading-relaxed">
                      Build sacred agreements, consent-based governance, and shared purpose for thriving communities.
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20 backdrop-blur-sm">
                    <h4 className="font-semibold text-indigo-400 mb-2 flex items-center gap-2">
                      <SacredFounderIcon size={18} />
                      Startup Studio
                    </h4>
                    <p className="text-white/60 text-xs leading-relaxed">
                      Create investor-ready business plans, 10-year pro forma projections, and pitch decks using proven frameworks.
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20 backdrop-blur-sm">
                    <h4 className="font-semibold text-fuchsia-400 mb-2 flex items-center gap-2">
                      <Feather size={16} />
                      Tome Machine
                    </h4>
                    <p className="text-white/60 text-xs leading-relaxed">
                      Write your novel, screenplay, memoir, or documentary with an 8-point story compass and AI assistance.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Sign in with Belonging Sessions - Below and smaller */}
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.8 }}
                className="mb-8"
              >
                <p className="text-xs text-white/40 mb-3 text-center">Already a Belonging Sessions member?</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBelongingSessionsLogin}
                  className="border border-[var(--accent)]/30 text-[var(--accent)]/80 hover:bg-[var(--accent)]/10 hover:border-[var(--accent)] px-6 py-2.5 text-sm rounded-full"
                  data-testid="sso-login-button"
                >
                  <LogInIcon size={16} className="mr-2" />
                  Sign in with Belonging Sessions
                </Button>
              </motion.div>

              {/* Learn More */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
              >
                <Button
                  variant="ghost"
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-white/50 hover:text-white/80 text-sm"
                >
                  Learn More
                </Button>
              </motion.div>

              {/* Compass Scroll indicator - replaces mouse graphic */}
              <CompassScrollIndicator />
            </>
          )}
        </AnimatePresence>
      </section>

      {/* Quote Section - Updated */}
      <section className="relative py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <GoldenSpiralIcon size={60} className="text-[var(--accent)] mx-auto mb-8 opacity-60" />
            <blockquote className="text-2xl md:text-4xl font-heading text-white/90 leading-relaxed mb-8">
              <span className="text-[var(--primary)]">vision</span>
              <span className="text-white/50 mx-3">+</span>
              <span className="text-[var(--accent)]">structure</span>
              <span className="text-white/50 mx-3">=</span>
              <span className="text-white">lasting impact</span>
            </blockquote>
            <p className="text-white/50 text-sm uppercase tracking-widest">Three Studios, One Platform</p>
          </motion.div>
        </div>
      </section>

      {/* THREE STUDIOS SECTION */}
      <section id="features" className="relative py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">
              Three Creative Studios
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Whether you're building a community, launching a startup, or writing your masterpiece — 
              Bliss Abode provides the compass and tools to guide your journey from vision to reality.
            </p>
          </motion.div>

          {/* Studio Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* Community Charter Studio */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              className="group relative"
            >
              <div className="h-full p-8 rounded-3xl bg-gradient-to-br from-emerald-900/30 to-teal-900/20 border border-emerald-500/20 backdrop-blur-sm hover:border-emerald-400/40 transition-all duration-500">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform duration-300">
                  <TreeOfLifeIcon size={32} className="text-white" />
                </div>
                <h3 className="font-heading text-2xl text-white mb-3">Community Charter</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-6">
                  Design intentional communities that last. Create sacred agreements, consent-based governance, 
                  and shared purpose using our 8-point compass methodology.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-xs text-emerald-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    8-Point Community Compass
                  </div>
                  <div className="flex items-center gap-2 text-xs text-emerald-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    BAST Framework (Belonging, Agency, Security, Trust)
                  </div>
                  <div className="flex items-center gap-2 text-xs text-emerald-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Consent-Based Governance Tools
                  </div>
                  <div className="flex items-center gap-2 text-xs text-emerald-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Shareable Community Documents
                  </div>
                </div>
                <Button
                  onClick={onEnter}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-full"
                >
                  Begin Your Charter
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </motion.div>

            {/* Startup Studio */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group relative"
            >
              <div className="h-full p-8 rounded-3xl bg-gradient-to-br from-indigo-900/30 to-blue-900/20 border border-indigo-500/20 backdrop-blur-sm hover:border-indigo-400/40 transition-all duration-500">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center mb-6 shadow-lg shadow-indigo-500/30 group-hover:scale-110 transition-transform duration-300">
                  <SacredFounderIcon size={32} className="text-white" />
                </div>
                <h3 className="font-heading text-2xl text-white mb-3">Startup Studio</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-6">
                  Launch investor-ready ventures. Create comprehensive business plans, 10-year pro forma 
                  projections, and pitch decks using proven startup frameworks.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-xs text-indigo-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                    8-Point Business Compass
                  </div>
                  <div className="flex items-center gap-2 text-xs text-indigo-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                    10-Year Financial Pro Forma
                  </div>
                  <div className="flex items-center gap-2 text-xs text-indigo-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                    One-Pager & Pitch Deck Generator
                  </div>
                  <div className="flex items-center gap-2 text-xs text-indigo-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                    Brand Name & Trademark Checker
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/startup')}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-full"
                >
                  Explore Startup Studio
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </motion.div>

            {/* Tome Machine */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group relative"
            >
              <div className="h-full p-8 rounded-3xl bg-gradient-to-br from-violet-900/30 to-fuchsia-900/20 border border-violet-500/20 backdrop-blur-sm hover:border-violet-400/40 transition-all duration-500">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center mb-6 shadow-lg shadow-violet-500/30 group-hover:scale-110 transition-transform duration-300">
                  <TomeMachineIcon size={32} className="text-white" />
                </div>
                <h3 className="font-heading text-2xl text-white mb-3">Tome Machine</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-6">
                  Write stories that matter. Craft your novel, screenplay, memoir, or documentary 
                  with an 8-point story compass and AI-powered writing assistance.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-xs text-violet-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                    8-Point Story Compass
                  </div>
                  <div className="flex items-center gap-2 text-xs text-violet-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                    Novel, Screenplay & Memoir Templates
                  </div>
                  <div className="flex items-center gap-2 text-xs text-violet-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                    Author Introspection Prompts
                  </div>
                  <div className="flex items-center gap-2 text-xs text-violet-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                    AI-Generated Outlines & First Chapters
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/tome')}
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white rounded-full"
                >
                  Start Your Story
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid - The Eight Foundations (Updated) */}
      <section className="relative py-24 px-4 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">
              The Eight Foundations
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Our philosophy across all three studios. BAST (Belonging, Agency, Security, Trust) forms the core, 
              while governance, connection, heart, and systems complete the architecture.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group"
              >
                <div className="h-full p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 hover:border-[var(--primary)]/30 transition-all duration-300">
                  <div className="w-14 h-14 rounded-xl bg-[var(--primary)]/20 flex items-center justify-center mb-4 group-hover:scale-110 group-hover:bg-[var(--primary)]/30 transition-all duration-300">
                    <feature.icon size={28} className="text-[var(--primary)]" />
                  </div>
                  <h3 className="font-heading text-lg text-white mb-2">{feature.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compass Comparison Section */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="max-w-5xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">
              Three Compasses, One Philosophy
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto mb-8">
              Each studio uses our 8-point compass methodology tailored to your specific creative journey.
            </p>
          </motion.div>

          {/* Compass Comparison Grid */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Community Compass */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 backdrop-blur-sm"
            >
              <h4 className="text-emerald-400 font-semibold mb-4 flex items-center gap-2">
                <Compass size={18} />
                Community Compass
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-emerald-300 font-bold">N</div>
                  <div className="text-white/60">Purpose</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-emerald-300 font-bold">NE</div>
                  <div className="text-white/60">Membership</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-emerald-300 font-bold">E</div>
                  <div className="text-white/60">Governance</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-emerald-300 font-bold">SE</div>
                  <div className="text-white/60">Economics</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-emerald-300 font-bold">S</div>
                  <div className="text-white/60">Space</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-emerald-300 font-bold">SW</div>
                  <div className="text-white/60">Culture</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-emerald-300 font-bold">W</div>
                  <div className="text-white/60">Relationships</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-emerald-300 font-bold">NW</div>
                  <div className="text-white/60">Legacy</div>
                </div>
              </div>
            </motion.div>

            {/* Startup Compass */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-6 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 backdrop-blur-sm"
            >
              <h4 className="text-indigo-400 font-semibold mb-4 flex items-center gap-2">
                <Compass size={18} />
                Startup Compass
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {startupCompassPoints.map((point) => (
                  <div key={point.direction} className="p-2 rounded bg-white/5 text-center">
                    <div className="text-indigo-300 font-bold">{point.direction}</div>
                    <div className="text-white/60">{point.title.split(' ')[0]}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Story Compass */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-6 rounded-2xl bg-violet-500/10 border border-violet-500/20 backdrop-blur-sm"
            >
              <h4 className="text-violet-400 font-semibold mb-4 flex items-center gap-2">
                <Feather size={18} />
                Story Compass
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-violet-300 font-bold">N</div>
                  <div className="text-white/60">Genesis</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-violet-300 font-bold">NE</div>
                  <div className="text-white/60">Character</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-violet-300 font-bold">E</div>
                  <div className="text-white/60">World</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-violet-300 font-bold">SE</div>
                  <div className="text-white/60">Conflict</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-violet-300 font-bold">S</div>
                  <div className="text-white/60">Structure</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-violet-300 font-bold">SW</div>
                  <div className="text-white/60">Voice</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-violet-300 font-bold">W</div>
                  <div className="text-white/60">Emotion</div>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <div className="text-violet-300 font-bold">NW</div>
                  <div className="text-white/60">Vision</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* M.ai.ry Section */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--primary)]/10 via-transparent to-[var(--primary)]/10" />
        <div className="max-w-4xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[var(--primary)] mb-8 shadow-lg shadow-[var(--primary)]/30">
              <MairyIcon size={48} className="text-white" />
            </div>
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
              Meet M.ai.ry
            </h2>
            <p className="text-xl text-white/70 mb-8 leading-relaxed">
              Your AI guide for community and business architecture. M.ai.ry combines deep wisdom with practical frameworks 
              to help you craft structures that will sustain your community or venture for generations.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-white/50">
              <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10">Conversational Guidance</span>
              <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10">One-Click Integration</span>
              <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10">Wisdom at Every Step</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section id="subscribe" className="relative py-24 px-4">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <VesicaIcon size={80} className="text-[var(--accent)] mx-auto mb-8 opacity-60" />
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
              Join the Movement
            </h2>
            <p className="text-white/60 mb-8">
              Subscribe to receive wisdom, updates, and invitations to the Belonging Sessions community.
            </p>

            {!subscribed ? (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-full px-6 py-6 focus:border-[var(--primary)] focus:ring-[var(--primary)]"
                  data-testid="subscribe-email-input"
                />
                <Button
                  type="submit"
                  disabled={isSubscribing}
                  className="bg-[var(--accent)] hover:bg-[var(--accent-600)] text-white px-8 py-6 rounded-full shadow-lg shadow-[var(--accent)]/20"
                  data-testid="subscribe-button"
                >
                  {isSubscribing ? 'Joining...' : 'Subscribe'}
                </Button>
              </form>
            ) : (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="p-6 rounded-2xl bg-[var(--primary)]/20 border border-[var(--primary)]/30"
              >
                <LotusIcon size={48} className="text-[var(--primary)] mx-auto mb-4" />
                <p className="text-white text-lg">Welcome to the journey.</p>
                <p className="text-white/60 text-sm mt-2">You'll hear from us soon.</p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-16 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-12">
            <div className="flex items-center gap-4">
              <MandalaIcon size={48} className="text-[var(--primary)]" />
              <div>
                <h4 className="font-heading text-xl text-white">Bliss Abode</h4>
                <p className="text-white/50 text-sm">Belonging Sessions Family</p>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <a href="https://blissabode.org" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[var(--primary)] transition-colors">
                Bliss Abode
              </a>
              <a href="https://belongingsessions.com" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[var(--primary)] transition-colors">
                Belonging Sessions
              </a>
              <a href="https://mairy.io" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[var(--primary)] transition-colors">
                M.ai.ry
              </a>
              <a href="https://elchme.com" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[var(--primary)] transition-colors">
                ElchmE
              </a>
              <a href="https://planetaryfam.com" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[var(--primary)] transition-colors">
                PlanetaryFam
              </a>
            </div>
          </div>

          <div className="text-center">
            <div className="flex justify-center gap-3 mb-6">
              {pillars.map((pillar) => (
                <span key={pillar.name} className="text-xs px-3 py-1 rounded-full bg-white/5 text-white/50 flex items-center gap-1">
                  <pillar.icon size={10} />
                  {pillar.name}
                </span>
              ))}
            </div>
            <p className="text-white/30 text-sm italic mb-4">
              "planetary confluence through shared remembrance"
            </p>
            <p className="text-white/20 text-xs">
              © {new Date().getFullYear()} Bliss Abode • Trust as Currency
            </p>
          </div>
        </div>
      </footer>
      
      {/* Ecosystem Navigation */}
      <EcosystemNav currentSite="community-forge" />
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
      
      {/* Projects Manager */}
      <ProjectsManager
        isOpen={showProjectsManager}
        onClose={() => setShowProjectsManager(false)}
        onSelectProject={(project) => {
          setShowProjectsManager(false);
          // Navigate based on project type
          if (project.project_type === 'startup') {
            navigate(`/startup?project=${project.id}`);
          } else if (project.project_type === 'tome') {
            navigate(`/tome?project=${project.id}`);
          } else {
            navigate(`/compass/${project.id}`);
          }
        }}
        onNewProject={(project) => {
          setShowProjectsManager(false);
          if (project.project_type === 'startup') {
            navigate(`/startup?project=${project.id}`);
          } else if (project.project_type === 'tome') {
            navigate(`/tome?project=${project.id}`);
          } else {
            navigate(`/compass/${project.id}`);
          }
        }}
      />
    </div>
  );
}
